'use client';

// IntersectionObserver: chart section ≥50% visible for ≥1s → chart_viewed.

import { useEffect, useRef } from 'react';
import { EVENTS, track } from '@/lib/analytics';

const MIN_VISIBLE_MS = 1000;
const THRESHOLD = 0.5;

export function useChartInView(chartId: string, enabled = true) {
  const ref = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !chartId) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= THRESHOLD) {
          if (viewedRef.current) return;
          timerRef.current = setTimeout(() => {
            if (viewedRef.current) return;
            viewedRef.current = true;
            track(EVENTS.chart_viewed, { chart_id: chartId });
          }, MIN_VISIBLE_MS);
        } else if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      },
      { threshold: THRESHOLD },
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [chartId, enabled]);

  return ref;
}
